import React from 'react';
import { observer } from 'mobx-react';
import { Paragraph, Text } from 'components';
import { Link } from 'core/routing';
import { clientStore } from 'apps/client/app';
import colors from 'styles/themes/wikifactory/styles/colors';
import type { SpaceModel } from 'apps/space/app';
import { buildRouteFromSpace } from 'apps/routes/utils';
import { Size, Variant } from './types';
import { mapSize } from './mapSize';

export interface SpaceLinkProps {
  space: SpaceModel;
  creatorSpace?: SpaceModel;
  mt?: number;
  label?: string;
  underlined?: boolean;
  size?: Size;
  variant?: Variant;
  preferWorkspaceLink?: boolean;
}

export const SpaceLink = observer(function SpaceLink({
  label,
  space,
  creatorSpace = null,
  underlined = true,
  variant = 'secondary',
  size = 'small',
  mt = 1,
  preferWorkspaceLink = false,
}: SpaceLinkProps) {
  const { signedInProfile } = clientStore;

  let sharedByLinkText = '';

  const isOwnProfileSpace = space.path === signedInProfile?.username;
  const isCreatorOwnProfileSpace =
    creatorSpace && creatorSpace.path === signedInProfile?.username;

  let text = label;
  if (!text) {
    if (isOwnProfileSpace || isCreatorOwnProfileSpace) {
      text = 'uploaded by';
    } else {
      text = 'shared by';
    }
  }

  const uploadedOrSharedByYou = isOwnProfileSpace || isCreatorOwnProfileSpace;
  if (uploadedOrSharedByYou) text += ' you';

  if (!isOwnProfileSpace) {
    if (space.spaceType === 'PROFILE') {
      sharedByLinkText = `@${space.content.slug}`;
    } else if (space.spaceType === 'INITIATIVE') {
      sharedByLinkText = `${space.content.title}`;
    }
  }

  let linkStyle = {};

  if (underlined) linkStyle = { textDecoration: 'underline' };
  if (variant === 'secondary') {
    linkStyle = { ...linkStyle, color: colors.wfGrey600 };
  }
  if (variant === 'primary') {
    linkStyle = { ...linkStyle, color: colors.primary };
  }

  const { name: linkName, params: linkParams } = buildRouteFromSpace(
    space,
    preferWorkspaceLink,
  );

  let creatorLinkName = null;
  let creatorLinkParams = null;
  let creatorLinkText = '';
  if (
    creatorSpace &&
    creatorSpace.id !== space.id &&
    space.spaceType === 'INITIATIVE' && // should always be true here
    !isCreatorOwnProfileSpace
  ) {
    creatorLinkText = creatorSpace.path;

    ({ name: creatorLinkName, params: creatorLinkParams } = buildRouteFromSpace(
      creatorSpace,
      false,
    ));
  }

  const hasCreatorAndSpace =
    (creatorLinkText || uploadedOrSharedByYou) && sharedByLinkText;

  return (
    <Paragraph
      size={mapSize(size)}
      display='inline-flex'
      mt={mt}
      style={{ alignItems: 'center' }}
    >
      <Text size='inherit' sx={{ flexShrink: 0 }}>
        {text}&nbsp;
      </Text>
      {!isOwnProfileSpace && (
        <>
          {creatorLinkName && (
            <>
              <Link
                name={creatorLinkName}
                params={creatorLinkParams}
                style={linkStyle}
              >
                {creatorLinkText}
              </Link>
              &nbsp;
            </>
          )}
          {hasCreatorAndSpace && <Text size='inherit'>{'to'}&nbsp;</Text>}
          <Link
            name={linkName}
            params={linkParams}
            id='super-link'
            style={linkStyle}
          >
            {sharedByLinkText}
          </Link>
        </>
      )}
    </Paragraph>
  );
});
